import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Send, MoreVertical, UserX, ShieldAlert, X } from 'lucide-react'
import { apiClient } from '../../api/client'
import type { ApiResponse, ChatMessage, ConversationDTO } from '../../types'
import { useAuthStore } from '../../store/authStore'
import { formatTime } from '../../utils/date'
import { detectChatRisk, chatRiskMessage } from '../../utils/chatSafety'
import { profileApi } from '../../api/profile'
import toast from 'react-hot-toast'

export default function ConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const navigate = useNavigate()
  const { userId: storedUserId } = useAuthStore()
  const [menuOpen, setMenuOpen] = useState(false)

  // Fallback: if userId not in store (old session before fix), fetch from profile
  const { data: myProfile } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => profileApi.getMyProfile().then((r) => r.data.data).catch(() => null),
    enabled: !storedUserId,
    staleTime: Infinity,
  })
  const userId = storedUserId ?? myProfile?.userId
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  // First-conversation safety banner — dismissible, remembered per conversation.
  const safetyKey = `safety_dismissed_${conversationId}`
  const [safetyDismissed, setSafetyDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(safetyKey) === '1',
  )
  const dismissSafety = () => {
    localStorage.setItem(safetyKey, '1')
    setSafetyDismissed(true)
  }

  // Fetch conversations list to get this conversation's metadata (name, avatar, profileId)
  const { data: conversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: () =>
      apiClient.get<ApiResponse<ConversationDTO[]>>('/chat').then((r) => r.data.data),
    staleTime: 30000,
  })
  const convo = conversations?.find((c) => c.conversationId === conversationId)

  const { data: messages, isLoading } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () =>
      apiClient.get<ApiResponse<ChatMessage[]>>(`/chat/${conversationId}/messages`)
        .then((r) => r.data.data),
    refetchInterval: 3000,
  })

  // Mark as read when opened
  useEffect(() => {
    if (conversationId) {
      apiClient.patch(`/chat/${conversationId}/read`).catch(() => {})
    }
  }, [conversationId])

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMut = useMutation({
    mutationFn: (content: string) =>
      apiClient.post(`/chat/${conversationId}/messages`, { content }),
    onSuccess: () => {
      setText('')
      qc.invalidateQueries({ queryKey: ['messages', conversationId] })
    },
    onError: (e: any) => {
      if (e?.response?.status === 402) {
        toast((t) => (
          <span className="flex items-center gap-2">
            Upgrade to keep chatting.{' '}
            <a href="/subscription" className="font-bold text-maroon-700 underline" onClick={() => toast.dismiss(t.id)}>
              Go Premium
            </a>
          </span>
        ), { duration: 6000 })
      } else if (e?.response?.status === 422 && e?.response?.data?.message) {
        // Blocked explicit/abusive content — show the reason, not a generic retry.
        toast.error(e.response.data.message)
      } else {
        toast.error('Message not sent. Please try again.')
      }
    },
  })

  const unmatchMut = useMutation({
    mutationFn: () => apiClient.delete(`/chat/${conversationId}`),
    onSuccess: () => {
      toast.success('Unmatched successfully')
      qc.invalidateQueries({ queryKey: ['conversations'] })
      navigate('/chat')
    },
    onError: () => toast.error('Failed to unmatch'),
  })

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault()
    if (!text.trim()) return
    sendMut.mutate(text.trim())
  }

  const handleUnmatch = () => {
    setMenuOpen(false)
    if (window.confirm('Are you sure you want to unmatch? This person will no longer appear in your matches or browse.')) {
      unmatchMut.mutate()
    }
  }

  const otherName   = convo?.otherUserName ?? 'Match'
  const otherAvatar = convo?.otherUserAvatar
  const otherProfileId = convo?.otherUserProfileId
  const initial = otherName.charAt(0).toUpperCase()

  return (
    <div className="flex flex-col h-screen md:h-[calc(100vh-0px)]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-ivory-25 border-b border-ivory-200 flex-shrink-0">
        <button onClick={() => navigate('/chat')} className="text-ink-400 hover:text-maroon-700">
          <ChevronLeft size={22} />
        </button>

        {/* Avatar — click to open profile */}
        {otherProfileId ? (
          <Link to={`/profile/${otherProfileId}`} className="flex-shrink-0">
            {otherAvatar ? (
              <img
                src={otherAvatar}
                alt={otherName}
                className="w-9 h-9 rounded-full object-cover ring-1 ring-gold-500/50"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-maroon-700 flex items-center justify-center text-champagne font-display font-semibold text-sm ring-1 ring-gold-500/50">
                {initial}
              </div>
            )}
          </Link>
        ) : (
          <div className="w-9 h-9 rounded-full bg-ivory-100 flex items-center justify-center text-maroon-700/60 font-display font-semibold text-sm flex-shrink-0 ring-1 ring-gold-500/40">
            {initial}
          </div>
        )}

        {/* Name — click to open profile */}
        <div className="flex-1 min-w-0">
          {otherProfileId ? (
            <Link to={`/profile/${otherProfileId}`} className="font-display font-semibold text-ink-900 text-base hover:text-maroon-700">
              {otherName}
            </Link>
          ) : (
            <p className="font-display font-semibold text-ink-900 text-base">{otherName}</p>
          )}
        </div>

        {/* Three-dot menu for unmatch */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-maroon-700 rounded-full hover:bg-ivory-100 transition-colors"
          >
            <MoreVertical size={18} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-9 z-20 bg-ivory-25 rounded-xl shadow-float border border-ivory-200 py-1 min-w-[150px]">
                <button
                  onClick={handleUnmatch}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-danger hover:bg-ivory-100 transition-colors"
                >
                  <UserX size={15} />
                  Unmatch
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-ivory-50">
        {/* First-conversation safety reminder */}
        {!safetyDismissed && (messages?.length ?? 0) <= 3 && (
          <div className="flex items-start gap-3 bg-maroon-700/5 border border-gold-500/40 rounded-2xl px-4 py-3">
            <ShieldAlert size={18} className="text-maroon-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-[12.5px] leading-relaxed text-ink-600">
              <span className="font-semibold text-ink-900">Stay safe.</span> Never send money or share
              bank/OTP details, and be wary of anyone who rushes you or asks to move off the app.{' '}
              <Link to="/safety" className="font-semibold text-maroon-700 underline">Safety tips</Link>
            </div>
            <button onClick={dismissSafety} aria-label="Dismiss" className="text-ink-400 hover:text-maroon-700 flex-shrink-0">
              <X size={16} />
            </button>
          </div>
        )}
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className="h-10 w-40 rounded-2xl bg-ivory-100 animate-pulse" />
              </div>
            ))}
          </div>
        ) : messages?.length ? (
          messages.map((msg) => {
            const isMine = msg.senderId === userId
            const risk = isMine ? null : detectChatRisk(msg.content)
            return (
              <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                    isMine
                      ? 'text-champagne rounded-br-sm'
                      : 'bg-ivory-25 text-ink-900 border border-ivory-200 rounded-bl-sm shadow-soft'
                  }`}
                  style={isMine ? { background: 'linear-gradient(135deg, #7A1B2E, #4E0F1F)' } : undefined}
                >
                  <p>{msg.content}</p>
                  <p className={`text-[10px] mt-1 ${isMine ? 'text-champagne/60' : 'text-ink-400'}`}>
                    {formatTime(msg.sentAt)}
                  </p>
                </div>
                {risk && (
                  <div className="mt-1 max-w-[75%] flex items-start gap-1 text-[11px] text-danger">
                    <ShieldAlert size={12} className="flex-shrink-0 mt-0.5" />
                    <span>{chatRiskMessage(risk)}</span>
                  </div>
                )}
              </div>
            )
          })
        ) : (
          <div className="text-center text-ink-400 text-sm pt-10">
            No messages yet. Say hello! 👋
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-center gap-2 px-4 py-3 bg-ivory-25 border-t border-ivory-200 flex-shrink-0">
        <input
          type="text"
          className="input flex-1"
          placeholder="Type a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <button
          type="submit"
          disabled={!text.trim() || sendMut.isPending}
          className="w-10 h-10 rounded-full bg-maroon-700 hover:bg-maroon-800 disabled:opacity-50 text-champagne ring-1 ring-inset ring-gold-500/40 flex items-center justify-center transition-colors flex-shrink-0"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  )
}
